import React, { useMemo } from 'react';

const DEFAULT_PROP = { name: '', type: 'text', required: false, show_name: true, as_title: false, meta: null };

export function normalize(struct) {
  const base = struct && typeof struct === 'object' ? struct : {};
  const cols = Array.isArray(base.columns) ? base.columns : [];
  const normalizedCols = cols.map((col) => {
    const properties = Array.isArray(col?.properties) ? col.properties : [];
    return {
      properties: properties.map((p) => {
        const type = ['image', 'text', 'line'].includes((p?.type || '').toLowerCase()) ? (p.type || '').toLowerCase() : DEFAULT_PROP.type;

        // meta is only supported for type 'line'. For other types meta MUST be null.
        let meta = null;
        if (type === 'line') {
          const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
          const allowedFontSizes = ['heading', 'sub-heading', 'text'];
          const fs = String(m.fontSize ?? '').toLowerCase();
          meta = {
            prefix: String(m.prefix ?? ''),
            suffix: String(m.suffix ?? ''),
            fontSize: allowedFontSizes.includes(fs) ? fs : 'text',
          };
        }

        return {
          name: String(p?.name ?? DEFAULT_PROP.name),
          type,
          required: p?.required !== undefined ? !!p.required : DEFAULT_PROP.required,
          show_name: p?.show_name !== undefined ? !!p.show_name : DEFAULT_PROP.show_name,
          as_title: p?.as_title !== undefined ? !!p.as_title : DEFAULT_PROP.as_title,
          meta,
        };
      }),
    };
  });
  return { columns: normalizedCols };
}

const GalleryItemStructureEditor = ({ value, onChange, className = '' }) => {
  const struct = useMemo(() => normalize(value), [value]);

  const emit = (next) => {
    onChange?.(normalize(next));
  };

  const addColumn = () => {
    const next = { columns: [...struct.columns, { properties: [] }] };
    emit(next);
  };

  const removeColumn = (idx) => {
    const nextCols = struct.columns.filter((_, i) => i !== idx);
    emit({ columns: nextCols });
  };

  const addProperty = (cIdx) => {
    const nextCols = struct.columns.map((c, i) => (i === cIdx ? { properties: [...c.properties, { ...DEFAULT_PROP }] } : c));
    emit({ columns: nextCols });
  };

  const removeProperty = (cIdx, pIdx) => {
    const nextCols = struct.columns.map((c, i) =>
      i === cIdx ? { properties: c.properties.filter((_, j) => j !== pIdx) } : c
    );
    emit({ columns: nextCols });
  };

  const updateProperty = (cIdx, pIdx, field, val) => {
    const nextCols = struct.columns.map((c, i) => {
      const props = c.properties.map((p, j) => {
        const isTarget = i === cIdx && j === pIdx;
        if (!isTarget) {
          // When as_title is being set to true, clear it on every other property
          if (field === 'as_title' && val === true) return { ...p, as_title: false };
          return p;
        }
        // Special handling for type change to enforce meta rules
        if (field === 'type') {
          const nextType = String(val || '').toLowerCase();
          if (nextType === 'line') {
            return { ...p, type: 'line', meta: p.meta && typeof p.meta === 'object'
                    ? { prefix: p.meta.prefix || '', suffix: p.meta.suffix || '', fontSize: p.meta.fontSize || 'text' }
                    : { prefix: '', suffix: '', fontSize: 'text' } };
          }
          return { ...p, type: nextType || 'text', meta: null, as_title: false };
        }
        return { ...p, [field]: val };
      });
      return { properties: props };
    });
    emit({ columns: nextCols });
  };

  const moveProperty = (cIdx, pIdx, dir) => {
    const props = [...(struct.columns[cIdx]?.properties || [])];
    const to = pIdx + dir;
    if (to < 0 || to >= props.length) return;
    const tmp = props[pIdx];
    props[pIdx] = props[to];
    props[to] = tmp;
    const nextCols = struct.columns.map((c, i) => (i === cIdx ? { properties: props } : c));
    emit({ columns: nextCols });
  };

  const containerStyle = { border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, margin: '12px 0' };
  const columnStyle = { border: '1px dashed #cbd5e1', borderRadius: 8, padding: 12, marginBottom: 12 };
  const headerCell = { fontSize: 12, color: '#64748b' };
// ... existing code ...
  return (
    <div className={className}>
      <style>{`
        .gis-fields {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: flex-end;
        }
        .gis-field {
          display: flex;
          flex-direction: column;
          gap: 3px;
          min-width: 120px;
        }
        .gis-field label {
          font-size: 11px;
          color: #64748b;
          margin: 0;
        }
        .gis-field-checkbox {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3px;
          min-width: 60px;
        }
        .gis-field-checkbox label {
          font-size: 11px;
          color: #64748b;
          margin: 0;
        }
        .gis-prop-card {
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 10px;
          margin-bottom: 8px;
          background: #fafafa;
        }
        .gis-prop-actions {
          display: flex;
          gap: 6px;
          justify-content: flex-end;
          margin-top: 8px;
        }
      `}</style>
      <div style={containerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <strong>Gallery item structure</strong>
          <button type="button" className="primary-button" onClick={addColumn}>Add column</button>
        </div>

        {struct.columns.length === 0 && (
          <div style={{ color: '#64748b', fontSize: 14, marginBottom: 8 }}>No columns yet. Add the first column.</div>
        )}

        {struct.columns.map((col, cIdx) => (
          <div key={cIdx} style={columnStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontWeight: 600 }}>Column #{cIdx + 1}</div>
              <button type="button" className="primary-button" onClick={() => removeColumn(cIdx)}>Remove column</button>
            </div>

            {(col.properties || []).map((p, pIdx) => (
              <div key={pIdx} className="gis-prop-card">
                <div className="gis-fields">
                  <div className="gis-field" style={{ flex: '1 1 160px' }}>
                    <label>Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={p.name}
                      onChange={(e) => updateProperty(cIdx, pIdx, 'name', e.target.value)}
                      placeholder="Property name"
                    />
                  </div>

                  <div className="gis-field" style={{ flex: '0 1 110px' }}>
                    <label>Type</label>
                    <select
                      className="form-control"
                      value={p.type}
                      onChange={(e) => updateProperty(cIdx, pIdx, 'type', e.target.value)}
                    >
                      <option value="line">line</option>
                      <option value="text">text</option>
                      <option value="image">image</option>
                    </select>
                  </div>

                  <div className="gis-field-checkbox">
                    <label>Required</label>
                    <input
                      type="checkbox"
                      checked={!!p.required}
                      onChange={(e) => updateProperty(cIdx, pIdx, 'required', e.target.checked)}
                    />
                  </div>

                  <div className="gis-field-checkbox">
                    <label>Show name</label>
                    <input
                      type="checkbox"
                      checked={!!p.show_name}
                      onChange={(e) => updateProperty(cIdx, pIdx, 'show_name', e.target.checked)}
                    />
                  </div>

                  {p.type === 'line' && (
                    <div className="gis-field-checkbox">
                      <label>As title</label>
                      <input
                        type="checkbox"
                        checked={!!p.as_title}
                        onChange={(e) => updateProperty(cIdx, pIdx, 'as_title', e.target.checked)}
                      />
                    </div>
                  )}

                  {p.type === 'line' && (
                    <>
                      <div className="gis-field" style={{ flex: '1 1 100px' }}>
                        <label>Prefix</label>
                        <input
                          type="text"
                          className="form-control"
                          value={p.meta?.prefix || ''}
                          onChange={(e) => updateProperty(cIdx, pIdx, 'meta', { ...(p.meta || {}), prefix: e.target.value })}
                          placeholder="Prefix"
                        />
                      </div>

                      <div className="gis-field" style={{ flex: '1 1 100px' }}>
                        <label>Suffix</label>
                        <input
                          type="text"
                          className="form-control"
                          value={p.meta?.suffix || ''}
                          onChange={(e) => updateProperty(cIdx, pIdx, 'meta', { ...(p.meta || {}), suffix: e.target.value })}
                          placeholder="Suffix"
                        />
                      </div>

                      <div className="gis-field" style={{ flex: '0 1 130px' }}>
                        <label>Font size</label>
                        <select
                          className="form-control"
                          value={p.meta?.fontSize || 'text'}
                          onChange={(e) => updateProperty(cIdx, pIdx, 'meta', { ...(p.meta || {}), fontSize: e.target.value })}
                        >
                          <option value="heading">heading</option>
                          <option value="sub-heading">sub-heading</option>
                          <option value="text">text</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>

                <div className="gis-prop-actions">
                  <button type="button" className="primary-button" onClick={() => moveProperty(cIdx, pIdx, -1)} title="Move up">↑</button>
                  <button type="button" className="primary-button" onClick={() => moveProperty(cIdx, pIdx, 1)} title="Move down">↓</button>
                  <button type="button" className="primary-button" onClick={() => removeProperty(cIdx, pIdx)}>Remove</button>
                </div>
              </div>
            ))}

            <div style={{ marginTop: 8 }}>
              <button type="button" className="primary-button" onClick={() => addProperty(cIdx)}>Add property</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GalleryItemStructureEditor;